import React from 'react'
import PropTypes from 'prop-types'
import { noop } from 'lodash'
import { Accordion, AccordionDetails, AccordionSummary, List, ListItem, ListItemText, Typography, useTheme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { ReactComponent as FrameIcon } from 'assets/FrameIcon.svg'

import T from 'T'
import { get } from 'utils/lodash'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import StyledBadge from 'components/notes/common/StyledBadge'
import NoteUserInfo from 'components/notes/common/NoteUserInfo'
import { HHAlert } from 'components/common/HHAlert'
import HistorySkeleton from './HistorySkeleton'

const SettingsHistoryAccordion = ({ versions = [], versionsLoading = false, hasVersions = false, getVersionChangeLines = noop }) => {
  const theme = useTheme()

  return (
    <Accordion variant="outlined" sx={{ border: 'none', '&:before': { display: 'none' } }} disableGutters>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          flexDirection: 'row-reverse',
          '& .MuiAccordionSummary-expandIconWrapper': { mr: 1 },
          '& .MuiAccordionSummary-content': { justifyContent: 'space-between', alignItems: 'center' },
        }}
      >
        <Typography variant="body1">{T.HISTORY}</Typography>
        {hasVersions && (
          <StyledBadge badgeContent={versions.length}>
            <FrameIcon />
          </StyledBadge>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List disablePadding>
          {versionsLoading && <HistorySkeleton rows={3} />}
          {!versionsLoading && !hasVersions && (
            <HHAlert severity="info" borderColor={theme.palette.info.light}>
              No history available.
            </HHAlert>
          )}
          {!versionsLoading &&
            hasVersions &&
            versions.map((version, index) => {
              const { firstName = '', lastName = '' } = get(version, 'updatedByUser', get(version, 'createdByUser', {}))
              const dateTime = get(version, 'updatedAt', get(version, 'createdAt'))
              const rev = get(version, 'rev')
              const { onlyDate = '', onlyTime = '' } = convertDateTimeToPreferedTimezone({ dateTime }) || {}
              const isLastRow = index === versions.length - 1
              const userName = firstName || lastName ? `${firstName} ${lastName}`.trim() : T.HAULER_HERO_SYSTEM
              const dateTimeText = onlyDate && onlyTime ? `${onlyDate} @${onlyTime}` : ''
              const changeLines = getVersionChangeLines(version)
              const secondaryText = dateTimeText ? `Updated by ${userName} | ${dateTimeText}` : `Updated by ${userName}`

              return (
                <ListItem key={get(version, 'rev', index)} divider={!isLastRow}>
                  <NoteUserInfo firstName={firstName} lastName={lastName} />
                  <ListItemText
                    primary={changeLines.map((line, i) => (
                      <React.Fragment key={`${rev}-${line}`}>
                        {i > 0 && <br />}
                        {line}
                      </React.Fragment>
                    ))}
                    secondary={secondaryText}
                    slotProps={{ primary: { variant: 'h6', fontWeight: 400 } }}
                  />
                </ListItem>
              )
            })}
        </List>
      </AccordionDetails>
    </Accordion>
  )
}

SettingsHistoryAccordion.propTypes = {
  versions: PropTypes.array,
  versionsLoading: PropTypes.bool,
  hasVersions: PropTypes.bool,
  getVersionChangeLines: PropTypes.func,
}

export default SettingsHistoryAccordion
